// Core articles data - will be moved to CMS later
import type { ArticleData } from './articleTypes';
import { calculateReadingTime } from '@/utils/seo';
import { getRandomGrantProfileWithSeed, getRandomFrancisProfileWithSeed } from '@/utils/profilePhotos';

// First 10 articles - keeping core ones in this file
export const coreArticles: ArticleData[] = [
  {
    id: 1,
    slug: "safety-monitoring-vs-spying-balancing-protection-privacy",
    title: "Safety monitoring vs. spying: Balancing protection and privacy for kids",
    excerpt: "Online safety monitoring poses an ethical dilemma for many parents. The question often arises about how to balance a child's right to privacy with a p...",
    content: `As our children spend increasing amounts of time online, parents face a challenging dilemma: How do we keep our kids safe in the digital world while respecting their growing need for privacy and independence?

## The Safety vs. Privacy Debate

This isn't a black and white issue. Both perspectives – the need for safety and the importance of privacy – have merit. The key is finding the right balance for your family's unique situation.

Children's online safety is a legitimate parental concern
Privacy is important for healthy development and trust
Age-appropriate monitoring strategies vary significantly
Open communication is more effective than secretive surveillance

## Building Trust Through Transparency

The most effective safety monitoring approaches involve transparency and collaboration rather than secretive surveillance. When children understand why monitoring exists and are involved in creating family digital rules, they're more likely to cooperate and develop good digital citizenship skills.

Trust is built through transparency, not surveillance. The goal is to teach children to make good decisions, not to catch them making bad ones.`,
    image: "/img/misc/misc-720.avif",
    imageAlt: "Father and son having a meaningful conversation at a park, representing balanced parenting and digital safety discussions",
    author: "Grant Callaghan",
    authorBio: "Grant is the founder of Joey and a father passionate about digital safety. With over a decade of experience in technology and parenting, he helps families navigate the digital world safely.",
    authorImage: "/img/misc/misc-720.avif",
    date: "2025-06-02",
    readTime: "8 min read",
    categories: ["Online Safety"],
    tags: ["Device protection", "screen time controls", "downtime limits"],
    featured: true,
    relatedArticles: [2, 5, 7],
    metaDescription: "Learn how to balance your child's online safety with their privacy needs. Expert tips on transparent monitoring vs. surveillance for digital parenting."
  },
  {
    id: 2,
    slug: "bullying-in-imessage-group-chats-and-snap",
    title: "Bullying in iMessage, group chats, and Snap: what it looks like and what to do next",
    excerpt: "Bullying rarely starts with one awful text. Spot the patterns in iMessage, group chats, and Snapchat — and use Joey's playbook to respond.",
    content: `Parents of 10–14s ask a simple question: how would I even know if bullying is happening on my child’s phone? It rarely starts with one awful message. It shows up as a run of small moments that add up — a name that sticks, a joke that travels, a new group without your child in it. The pattern is the clue, not the single text.

In one-to-one iMessage chats, the tone often shifts first. The language gets sharp. “You’re so d*mb lol” lands on a Monday, then again on Thursday, then again the week after. In group chats, reaction storms and spin-off groups are common tells. Silence after your child speaks is part of the pattern too.

Snapchat adds disappearing messages and streak pressure. Private stories can flip from fun to isolating when a child is left out or becomes the punchline. Screenshots get saved and recycled later. Even though Joey analyses iMessage today, coaching has to follow kids into Snap.

The data shows bullying is far more common than parents think. eSafety’s latest survey found more than half of Australian 10–17s have been cyberbullied, and UNICEF’s global work puts the figure at one in three. Pew’s U.S. research says nearly half of teens experience at least one cyberbullying behaviour, with name-calling still the leading pattern.

When Joey surfaces bullying signals, focus on patterns, not every message. Review the alert summary, scan the How’s My Child report, and prioritise calm check-ins with boundary-setting scripts. Save evidence, report in-app, and escalate to eSafety if platforms do not act.

Learn more about [Smart Alerts](/features/smart-alerts) and [How’s My Child report](/features/hows-my-child-report) to keep the coaching loop strong.`,
    image: "/assets/articles/bullying-imessage-snap-hero.jpg",
    imageAlt: "iMessage and Snapchat chat bubbles highlighting bullying patterns on a phone screen",
    author: "Joey Team",
    authorBio: "The Joey editorial team curates resources to help families navigate bullying, privacy, and digital wellbeing with practical scripts and product guidance.",
    authorImage: "/img/misc/misc-720.avif",
    date: "2025-11-06",
    readTime: "6 min read",
    categories: ["Online Safety"],
    tags: ["Bullying", "iMessage", "Snapchat", "Group chats"],
    featured: false,
    relatedArticles: [1, 7, 8],
    metaDescription: "How bullying shows up in iMessage, group chats, and Snap. Parent scripts, what Joey flags, and when to escalate."
  },  {
    id: 3,
    slug: "social-media-ban-under-16-australia-kids-perspective",
    title: "The Social Media Ban Is Coming: What Do Kids Really Think?",
    excerpt: "Australia's social media ban for under-16s starts Dec 2025. Hear what kids think, what eSafety says, and what parents should know.",
    content: `## At a Glance

• Australia's social media ban for under-16s begins December 10, 2025.
• eSafety Commissioner Julie Inman Grant says kids' accounts will be deactivated, not deleted.
• YouTube is now included in the ban.
• Critics warn of privacy risks and kids shifting to less safe apps.
• Here's how kids (like me) actually feel about it.

Hi, Francis here 👋. You've probably heard that in Australia, starting this December, kids under 16 won't be able to use social media apps like Snapchat, TikTok, Instagram — and now even YouTube.

It's one of the strictest bans in the world, and everyone at school is talking about it. Here's what it feels like from a kid's perspective — and what's actually happening with the rollout.

## Why the ban feels so big

For kids my age, social media isn't just apps — it's how we:

• Chat with friends after school, especially in group chats.
• Share funny stuff like memes, streaks, or short videos.
• Keep up with sports and gaming highlights.

So when you hear that apps we use every day might suddenly disappear, it feels like being told: "No more playground after December."

## What kids are saying

I've been asking my friends about it, and here's what I'm hearing most:

**"We'll just move to iMessage."** Many kids say they'll go back to basics — texting, FaceTime, maybe WhatsApp.

**"It's annoying, but not the end of the world."** Most of us are more frustrated than panicked.

**"There'll always be a workaround."** Some kids are already joking about VPNs or using their parents' logins.

**"New apps will pop up."** That's the biggest expectation. If something gets banned, kids think another app will take its place.

## What the rules actually say

The law — the Online Safety Amendment (Social Media Minimum Age) Act 2024 — comes into force on 10 December 2025. Social media companies will have to take "reasonable steps" to deactivate accounts of kids under 16 or risk fines of up to A$49.5 million.

Julie Inman Grant, Australia's eSafety Commissioner, explained it this way:

<img src="/img/misc/misc-720.avif" alt="Julie Inman Grant, Australia's eSafety Commissioner" style="width: 300px; height: 200px; object-fit: cover; border-radius: 12px; margin: 16px 0;" />

> "We know 95% of Australian 10- to 15-year-olds currently hold at least one social media account. Companies must detect and deactivate these accounts from 10 December. But don't expect all under-16s to be scrubbed overnight. It's more accurate to frame this as a social media delay."

## How will it be enforced?

The government tested different age-assurance technologies earlier this year, including ID checks and AI-based age estimation. The results showed that some tools worked well, but others had big issues — especially around accuracy and privacy.

• Platforms must use these tools to verify age, but how each company does it is still being decided.
• The eSafety office is currently consulting with tech companies, experts, and even kids about what "reasonable steps" should look like.
• Accounts won't be deleted right away — they'll be deactivated, meaning locked until the user can prove they're 16 or older.

## The criticism

Not everyone thinks the ban is a good idea. Some experts and parents are worried that:

• Kids will move to less safe, unregulated apps instead of mainstream ones.
• Age-verification tools could store too much data or misidentify kids, blocking some unfairly.
• It won't solve core issues like bullying or harmful content — those problems will still exist.

As a kid, I can see both sides. I get why adults want us safe. But I also know that banning doesn't stop kids being online — it just changes where we go.

## My take

For me and my friends, this isn't the end of social media. It just means:

• We'll probably move more to [iMessage monitoring for parents](/features/iphone-monitoring), FaceTime, and private chats.
• Some kids will definitely try workarounds.
• The real question is whether we'll be safer — or if it just pushes us into riskier spaces.

## Why talking matters

Even if the ban goes through as planned, what will matter most is how families talk about it.

If you're a parent, try asking your kid:

• "How do you feel about not being able to use Snap/TikTok anymore?"
• "What apps do you think you'll use instead?"
• "Do you think this will change how your friends hang out?"

Change is easier when kids know their parents are listening — not just enforcing new rules.

## Final thought

The ban is coming. Kids can't control that. What we can control is how we adapt. Some things will be frustrating, but maybe we'll also find healthier ways to connect.

For parents: don't assume the ban solves everything. Kids will still be online — and they'll still need your help to stay safe, happy, and balanced in whatever new apps they use. Learn more about [How's My Child](/features/hows-my-child-report) to help support your kids through these changes.

## Frequently Asked Questions

**Q: When does the under-16 social media ban start in Australia?**
A: From 10 December 2025, platforms must deactivate under-16 accounts or risk heavy fines.

**Q: Which apps are included in the ban?**
A: Snapchat, TikTok, Instagram, X, Facebook, Reddit, LinkedIn — and now YouTube. Messaging apps like iMessage and WhatsApp are not banned.

**Q: Will kids get in trouble for using banned apps?**
A: No. The law targets social media companies, not children. Kids won't be fined or punished.`,
    image: "/img/misc/misc-720.avif",
    imageAlt: "Illustration showing popular social media app icons like TikTok, Snapchat, Instagram, and YouTube locked with padlocks, alongside teenagers chatting on iMessage in a park setting. Visual highlights Australia's under-16 social media ban starting December 2025.",
    author: "Francis Callaghan", 
    authorBio: "Francis is a teen writer and digital native who provides unique insights into how young people experience technology and social media.",
    authorImage: getRandomFrancisProfileWithSeed("francis-article-3"),
    date: "2025-01-10",
    readTime: "7 min read",
    categories: ["Insights"],
    tags: ["Australia", "Social media ban", "Teen voice", "eSafety", "Online safety"],
    featured: true,
    relatedArticles: [1, 2],
    metaDescription: "Australia's social media ban for under-16s starts Dec 2025. Hear what kids think, what eSafety says, and what parents should know."
  }
];

// cleaned summaryPoints
export const summaryPoints = [
  "Children's online safety is a legitimate parental concern",
  "Privacy is important for healthy development and trust",
  "Age-appropriate monitoring strategies vary significantly",
  "Open communication is more effective than secretive surveillance"
];

// cleaned frequentQuestions
export const frequentQuestions = [
  "Were there some new friends in my son's circle that may not be a great fit for him?",
  "Were older siblings in the social circle sharing inappropriate content?",
  "Are these kids playing nice online? Would my son even tell me if kids were being mean?",
  "Was my son lonely?",
  "I want to trust my son and let him take appropriate risks without being a helicopter parent, but how do I know how much leeway we can give him and his mates? The kids might present one way to us parents (all charm) but what is the reality?"
];



